// MARK://dhk8/pos/lib/clients/offline
// Operator-facing view of the offline outbox + connection state, for /offline/cola
// ("Conexión"). Two concerns:
//   1. Connection state — are we online (cloud-direct / lan-relay) or offline?
//      Derived from `api-origin.ts` (the resolved operating mode) plus
//      `navigator.onLine`. Last-sync time is tracked in-memory here.
//   2. Pending mutations queue — a flattened view of the IndexedDB outbox with a
//      per-entry status (pendiente / reintentando / error). The real outbox lives
//      in `../offline-queue.ts`; until that module exposes a list+status surface,
//      this client returns the seed queue under PREVIEW_STUB and an empty queue
//      otherwise (the page already reads the raw IndexedDB store for the legacy
//      table; this client is the new richer view).
//
// TODO backend/infra: when `offline-queue.ts` grows `list()` + per-entry status,
// wire `getQueue()` to it and drop the seed branch.

import { operatingMode, isLanRelay } from '../api-origin.js'
import { PREVIEW_STUB, SEED_OFFLINE_QUEUE, type SeedOfflineEntry } from '../seed-preview.js'

export type ConnMode = 'cloud-direct' | 'lan-relay' | 'offline'

export interface ConnState {
  mode: ConnMode
  /** ISO timestamp of the last successful sync, or null if never. */
  lastSyncAt: string | null
}

export type QueueItemStatus = 'pendiente' | 'reintentando' | 'error'

export type QueueItemKind = 'comanda' | 'pago' | 'cierre' | 'otro'

export interface QueueItem {
  id: string
  kind: QueueItemKind
  /** Human label, e.g. "Comanda C-8999 · enviar a cocina". */
  label: string
  /** Epoch ms when the mutation was queued. */
  queuedAt: number
  status: QueueItemStatus
  attempts: number
  /** Present when `status === 'error'`. */
  errorMessage?: string
}

// ── in-memory state (preview / fallback) ───────────────────────────────────

let lastSyncAt: string | null = PREVIEW_STUB
  ? new Date(Date.now() - 6 * 60_000).toISOString()
  : null

let previewQueue: QueueItem[] = PREVIEW_STUB ? SEED_OFFLINE_QUEUE.map(fromSeed) : []

function fromSeed(e: SeedOfflineEntry): QueueItem {
  return {
    id: e.id,
    kind: e.kind,
    label: e.label,
    queuedAt: Date.now() - e.ageMinutes * 60_000,
    status: e.status,
    attempts: e.attempts,
    ...(e.errorMessage ? { errorMessage: e.errorMessage } : {}),
  }
}

// ── connection state ───────────────────────────────────────────────────────

export function getConnState(): ConnState {
  const online = typeof navigator === 'undefined' ? true : navigator.onLine
  const mode: ConnMode = !online ? 'offline' : isLanRelay() ? 'lan-relay' : operatingMode()
  return { mode, lastSyncAt }
}

// ── queue ──────────────────────────────────────────────────────────────────

/** The pending-mutations queue, newest first. */
export async function getQueue(): Promise<QueueItem[]> {
  if (PREVIEW_STUB) return [...previewQueue].sort((a, b) => b.queuedAt - a.queuedAt)
  // Real outbox listing not exposed yet — see TODO at top.
  return []
}

/** Retry every queued mutation. In the preview this clears the queue (success)
 * except entries whose seed error message contains "permanente" (kept as error). */
export async function retryAll(): Promise<{ remaining: number }> {
  if (PREVIEW_STUB) {
    previewQueue = previewQueue.filter(
      (it) => it.status === 'error' && /permanente/i.test(it.errorMessage ?? ''),
    )
    if (previewQueue.length === 0) lastSyncAt = new Date().toISOString()
    return { remaining: previewQueue.length }
  }
  return { remaining: 0 }
}

/** Flush ("Vaciar registro local") — drop every queued mutation unconditionally. */
export async function flushQueue(): Promise<{ flushed: number }> {
  const n = previewQueue.length
  previewQueue = []
  return { flushed: n }
}

/** Test/preview helper: reset the in-memory preview queue to the seed. */
export function __resetPreviewQueue(): void {
  previewQueue = PREVIEW_STUB ? SEED_OFFLINE_QUEUE.map(fromSeed) : []
  lastSyncAt = PREVIEW_STUB ? new Date(Date.now() - 6 * 60_000).toISOString() : null
}
